import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { useGustoStatus, useConnectGusto, useDisconnectGusto } from '../../hooks';
import {
  Link2,
  Unlink,
  Loader2,
  AlertCircle,
  Info,
} from 'lucide-react';

export const GustoConnectionCard: React.FC = () => {
  const { t } = useTranslation('admin');
  const { data: status, isLoading, error } = useGustoStatus();
  const connectGusto = useConnectGusto();
  const disconnectGusto = useDisconnectGusto();

  if (isLoading) {
    return (
      <Card data-testid="gusto-connection-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            <CardTitle>{t('gusto.title')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="gusto-connection-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            <CardTitle>{t('gusto.title')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t('gusto.errorLoading')}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const isConfigured = status?.serviceConfigured ?? false;

  return (
    <Card data-testid="gusto-connection-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            <CardTitle>{t('gusto.title')}</CardTitle>
          </div>
          {status && (
            <Badge className={status.connected ? 'bg-green-500' : undefined} variant={status.connected ? 'default' : 'secondary'}>
              {status.connected ? t('gusto.statusConnected') : t('gusto.statusDisconnected')}
            </Badge>
          )}
        </div>
        <CardDescription>{t('gusto.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isConfigured && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>{t('gusto.notConfigured')}</AlertDescription>
          </Alert>
        )}
        {status?.connected ? (
          <>
            <div className="border rounded-lg p-4 space-y-3">
              {status.connectedByUserEmail && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('gusto.connectedBy')}</span>
                  <span className="font-medium">{status.connectedByUserEmail}</span>
                </div>
              )}
              {status.connectedAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('gusto.connectedAt')}</span>
                  <span className="font-medium">{new Date(status.connectedAt).toLocaleString()}</span>
                </div>
              )}
              {status.expiresAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('gusto.expiresAt')}</span>
                  <span className="font-medium">{new Date(status.expiresAt).toLocaleString()}</span>
                </div>
              )}
              {status.scope && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('gusto.scope')}</span>
                  <span className="font-medium font-mono text-xs">{status.scope}</span>
                </div>
              )}
            </div>
            <Button
              variant="destructive"
              onClick={() => disconnectGusto.mutate()}
              disabled={disconnectGusto.isPending}
              className="gap-2"
              data-testid="gusto-disconnect-button"
            >
              {disconnectGusto.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('gusto.disconnecting')}
                </>
              ) : (
                <>
                  <Unlink className="h-4 w-4" />
                  {t('gusto.disconnect')}
                </>
              )}
            </Button>
            {disconnectGusto.isError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{t('gusto.disconnectError')}</AlertDescription>
              </Alert>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{t('gusto.connectDescription')}</p>
            <Button
              onClick={() => connectGusto.mutate()}
              disabled={connectGusto.isPending || !isConfigured}
              className="gap-2"
              data-testid="gusto-connect-button"
            >
              {connectGusto.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('gusto.connecting')}
                </>
              ) : (
                <>
                  <Link2 className="h-4 w-4" />
                  {t('gusto.connect')}
                </>
              )}
            </Button>
            {connectGusto.isError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{t('gusto.connectError')}</AlertDescription>
              </Alert>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
